/**
 * Types mirroring the FastAPI schemas in `server/app/`.
 *
 * These are hand-written rather than generated so the shell can be built
 * before every router exists, but they are transcriptions, not inventions:
 * each block below names the module it mirrors. When a server schema changes,
 * this file changes with it.
 */

// ---------------------------------------------------------------------------
// Enumerations — server/app/models/core.py
// ---------------------------------------------------------------------------

/**
 * The four offices, exactly. `EM` is EUIPO and is never spelled `ep` or `eu`
 * (docs/IMPLEMENTATION_PLAN.md §3.1); `GB` and UK are one office and normalisation
 * happens on read, never by storing both.
 */
export const JURISDICTIONS = ['US', 'GB', 'EM', 'IN'] as const
export type Jurisdiction = (typeof JURISDICTIONS)[number]

/**
 * How an office is written on screen. `GB` renders as "UK" in the UI *only* —
 * the value posted back is always `GB`.
 */
const JURISDICTION_LABELS: Record<Jurisdiction, string> = {
  US: 'US',
  GB: 'UK',
  EM: 'EU',
  IN: 'IN',
}

/** The office's display label. Storage is unaffected. */
export function jurisdictionLabel(jurisdiction: Jurisdiction): string {
  return JURISDICTION_LABELS[jurisdiction]
}

/** The long form, for tooltips and selects. */
export const JURISDICTION_OFFICES: Record<Jurisdiction, string> = {
  US: 'USPTO',
  GB: 'UKIPO',
  EM: 'EUIPO',
  IN: 'India TMR',
}

export const PROCEEDING_TYPES = [
  'EXAM_OA',
  'OPPOSITION',
  'CANCELLATION',
  'APPEAL',
  'OTHER',
] as const
export type ProceedingType = (typeof PROCEEDING_TYPES)[number]

/**
 * Only `EXAM_OA` is implemented through P8; the API answers 501 for the rest
 * (plan §5 P0). The shell offers no other option in a create form.
 */
export const IMPLEMENTED_PROCEEDING_TYPES: readonly ProceedingType[] = ['EXAM_OA']

export const MATTER_LIFECYCLES = [
  'INTAKE',
  'CONFIRMED',
  'ANALYSIS',
  'DRAFTING',
  'REVIEW',
  'APPROVED',
  'DISPATCHED',
  'CLOSED',
  'DECLINED',
] as const
export type MatterLifecycle = (typeof MATTER_LIFECYCLES)[number]

export const SOURCE_CHANNELS = ['manual', 'register', 'wipo_ftp', 'collector'] as const
export type SourceChannel = (typeof SOURCE_CHANNELS)[number]

/**
 * What a screen calls a matter's stored proceeding code (`EXAM_OA`) instead of
 * the code. Typed over every {@link ProceedingType} so a new one cannot be added
 * without deciding what a person reads for it.
 */
export const PROCEEDING_LABELS: Record<ProceedingType, string> = {
  EXAM_OA: 'Examination report',
  OPPOSITION: 'Opposition',
  CANCELLATION: 'Cancellation',
  APPEAL: 'Appeal',
  OTHER: 'Other proceeding',
}

/** What a person reads for a matter's coarse state (`REVIEW`), not the enum. */
export const LIFECYCLE_LABELS: Record<MatterLifecycle, string> = {
  INTAKE: 'Intake',
  CONFIRMED: 'Facts confirmed',
  ANALYSIS: 'Analysis',
  DRAFTING: 'Drafting',
  REVIEW: 'In review',
  APPROVED: 'Approved',
  DISPATCHED: 'Dispatched',
  CLOSED: 'Closed',
  DECLINED: 'Declined',
}

/** How the matter arrived, in words (`wipo_ftp` is not one). */
export const SOURCE_CHANNEL_LABELS: Record<SourceChannel, string> = {
  manual: 'Entered by hand',
  register: 'Register watch',
  wipo_ftp: 'WIPO feed',
  collector: 'Collector',
}

// A value the server may add before this build learns it prints as itself, not
// as "undefined": the lookups below take `string` and fall back to the input.
const lookup = (labels: Readonly<Record<string, string>>, value: string): string =>
  (Object.prototype.hasOwnProperty.call(labels, value) ? labels[value] : undefined) ?? value

export const proceedingLabel = (value: string): string => lookup(PROCEEDING_LABELS, value)
export const lifecycleLabel = (value: string): string => lookup(LIFECYCLE_LABELS, value)
export const sourceChannelLabel = (value: string): string => lookup(SOURCE_CHANNEL_LABELS, value)

export const PARTY_ROLES = ['APPLICANT', 'OFFICE', 'OPPONENT', 'TRIBUNAL'] as const
export type PartyRole = (typeof PARTY_ROLES)[number]

export const BLOB_KINDS = ['primary', 'supporting', 'generated'] as const
export type BlobKind = (typeof BLOB_KINDS)[number]

export const USER_ROLES = ['analyst', 'bd', 'admin'] as const
export type UserRole = (typeof USER_ROLES)[number]

export const API_KEY_SCOPES = ['matters:write', 'matters:read', 'research:read'] as const
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number]

export const AGENT_BRANDINGS = ['cobrand', 'whitelabel', 'arctic'] as const
export type AgentBranding = (typeof AGENT_BRANDINGS)[number]

/**
 * Three roles are seeded and live; `classifier` and `vlm_ocr` are v2.2 and
 * deliberately absent from the database (plan §3). Listed here because the
 * admin screen renders whatever `/api/admin/llm-config` returns.
 */
export const LLM_ROLES = ['reasoner', 'judge', 'embedder', 'classifier', 'vlm_ocr'] as const
export type LlmRole = (typeof LLM_ROLES)[number]

/**
 * One, and it was five. `uspto_odp`, `euipo`, `ukipo` and `inpass` were
 * placeholders for a register fetch that was never built, so the admin screen
 * took four credentials nothing would ever spend. Migration 0010 removed them
 * and `PUT /api/admin/service-keys/uspto_odp` now 422s like any other unknown
 * name.
 */
export const SERVICE_KEY_NAMES = ['smtp', 'firecrawl'] as const
export type ServiceKeyName = (typeof SERVICE_KEY_NAMES)[number]

/** The pipeline's fixed stage vocabulary (plan §0). */
export const PIPELINE_STAGES = [
  's0_intake',
  's1_parse',
  's1b_marks',
  's2_normalize',
  's3_analyze',
  's4_research',
  's5_generate',
  's6_review',
] as const
export type PipelineStage = (typeof PIPELINE_STAGES)[number]

// ---------------------------------------------------------------------------
// Auth — server/app/routers/auth.py
// ---------------------------------------------------------------------------

/** `UserOut`. */
export interface User {
  id: string
  email: string
  role: UserRole
  active: boolean
  last_login_at: string | null
}

/** `RequestCodeOut`. Identical for a known and an unknown address by design. */
export interface RequestCodeResponse {
  accepted: boolean
  expires_at: string
  /** Present outside production only — there is no mail transport until P9.1. */
  debug_code?: string | null
}

/** `SessionOut` — the body of a successful `POST /api/auth/verify`. */
export interface SessionResponse {
  user: User
  expires_at: string
}

/**
 * `MeOut`. One shape for a human session and for an API key, distinguished by
 * `kind`; `scopes` is authoritative, so the client never keeps a second copy
 * of the role-to-scope map.
 */
export interface Me {
  kind: string
  actor: string
  email: string | null
  role: UserRole | null
  scopes: string[]
  user: User | null
  api_key_name: string | null
  session_expires_at: string | null
}

// ---------------------------------------------------------------------------
// Matters — server/app/routers/matters.py
// ---------------------------------------------------------------------------

/** `MatterResponse`. */
export interface Matter {
  id: string
  jurisdiction: Jurisdiction
  proceeding_type: ProceedingType
  lifecycle: MatterLifecycle
  source_channel: SourceChannel
  relationship_known: boolean
  agent_id: string | null
  application_number: string | null
  mark_text: string | null
  applicant_name: string | null
  deadline_date: string | null
  deadline_verified: boolean
  /**
   * Whose matter this is — an email for a person, `key:<name>` for an
   * integration, `null` for one only administrators can see. A non-admin only
   * ever receives matters they already own, so for them this is always their
   * own address; it is here for the admin case, where the list spans people.
   */
  owner_actor: string | null
  created_at: string
  updated_at: string
}

/** `MatterEventResponse` — the append-only log (I12). */
export interface MatterEvent {
  id: number
  matter_id: string
  ts: string
  stage: string
  event: string
  actor: string | null
  payload: Record<string, unknown>
}

/** `MatterDetailResponse`. */
export interface MatterDetail extends Matter {
  events: MatterEvent[]
}

/** `MatterCreateRequest`. The id is allocated server-side and is not accepted. */
export interface MatterCreateRequest {
  jurisdiction: Jurisdiction
  proceeding_type?: ProceedingType
  source_channel?: SourceChannel
  relationship_known?: boolean
  agent_id?: string | null
  application_number?: string | null
  mark_text?: string | null
  applicant_name?: string | null
  deadline_date?: string | null
}

// ---------------------------------------------------------------------------
// Admin — server/app/routers/admin_users.py and the config router
// ---------------------------------------------------------------------------

/** `ApiKeyOut`. Note the absence of `key_hash` — I14. */
export interface ApiKey {
  id: string
  name: string
  prefix: string
  scopes: string[]
  active: boolean
  last_used_at: string | null
  created_at: string
}

/**
 * `ApiKeyCreatedOut`. The only response in the product that carries a
 * plaintext credential, and it carries it once (I14).
 */
export interface ApiKeyCreated extends ApiKey {
  key: string
}

export interface UserCreateRequest {
  email: string
  role?: UserRole
}

export interface UserPatchRequest {
  role?: UserRole
  active?: boolean
}

export interface ApiKeyCreateRequest {
  name: string
  scopes: ApiKeyScope[]
}

/**
 * Where a credential is coming from. `none` means the role cannot call out at
 * all — a cleared database key falls back to the environment variable (I14).
 */
export type KeySource = 'database' | 'environment' | 'none'

/** One `fallback_order` link. Used on hard failure only, never on 429 (I11). */
export interface FallbackEntry {
  provider: string
  model_id: string
  base_url?: string | null
}

/**
 * `LlmConfigOut`. There is no key field and there never will be: `has_key` and
 * `key_source` are all a screen can know about the credential (I14).
 */
/**
 * `auto` leaves the model to decide; `off` asks the provider not to think.
 *
 * It matters because `max_tokens` is one budget shared between the thinking
 * and the answer: a role sized for a model that does not reason truncates
 * every call the moment it is repointed at one that does.
 */
export type ReasoningMode = 'auto' | 'off'

export interface LlmConfig {
  role: LlmRole
  provider: string
  model_id: string
  max_tokens: number
  timeout_s: number
  reasoning: ReasoningMode
  fallback_order: FallbackEntry[]
  enabled: boolean
  has_key: boolean
  key_source: KeySource
  updated_at: string
}

/**
 * `LlmConfigIn` — a full upsert (`PUT /api/admin/llm-config/{role}`), so send
 * every field, not a patch. The server forbids unknown keys.
 */
export interface LlmConfigUpdateRequest {
  provider: string
  model_id: string
  max_tokens: number
  timeout_s: number
  /**
   * Required in practice, though the server defaults it. A full upsert that
   * omits it resets the role to `auto`, so a screen saving an unrelated field
   * would silently switch the thinking back on.
   */
  reasoning: ReasoningMode
  fallback_order?: FallbackEntry[]
  enabled?: boolean
  /** Write-only. Omit to leave unchanged; empty string clears it to the env var. */
  api_key?: string | null
}

/** `LlmTestOut` — a real round-trip through the adapter, not a ping. */
export interface LlmConfigTestResult {
  ok: boolean
  latency_ms?: number | null
  echo?: Record<string, unknown> | null
  provider?: string | null
  model_id?: string | null
  attempts?: number | null
  fallback_index?: number | null
  /** Scrubbed server-side before it is set — safe to display. */
  error?: string | null
}

/** `ServiceKeyOut`. The key itself is write-only (I14). */
export interface ServiceKey {
  name: ServiceKeyName
  has_key: boolean
  key_source: KeySource
  updated_at: string
}

/** `ServiceKeyIn`. Empty string clears the stored key back to the env var. */
export interface ServiceKeyUpdateRequest {
  api_key: string | null
}

/** An instructing agent. */
export interface Agent {
  id: string
  name: string
  email: string
  branding: AgentBranding
  fee_override: string | null
  relationship_known: boolean
  created_at: string
}

// ---------------------------------------------------------------------------
// Health — server/app/main.py
// ---------------------------------------------------------------------------

export interface HealthResponse {
  status: string
  app: string
  version: string
  environment: string
}

// ---------------------------------------------------------------------------
// Pipeline runs — server/app/pipeline/{stages,runner}.py, over the three
// routes of `/api/matters/{id}/pipeline/runs` and `/api/pipeline/runs/{id}`
// ---------------------------------------------------------------------------

/**
 * What kind of response the report calls for (`MatterKind`, models/core.py).
 *
 * `s2_normalize` derives it; until that port lands the New case screen asks a
 * person, which is why the vocabulary is here rather than only on the server.
 */
export const MATTER_KINDS = ['substantive', 'specification'] as const
export type MatterKind = (typeof MATTER_KINDS)[number]

/** Where a run is in its own life. Not a verdict — see {@link PipelineVerdict}. */
export const PIPELINE_RUN_STATUSES = ['running', 'finished', 'failed'] as const
export type PipelineRunStatus = (typeof PIPELINE_RUN_STATUSES)[number]

/**
 * `Verdict` — what became of the matter, in the runner's own words.
 *
 * The set is closed and each member means something a screen must not blur:
 *
 * - `drafted` — a draft exists and the gate did not block it. The **only**
 *   exportable end (`Verdict.exportable`).
 * - `blocked` — a draft exists and a deterministic gate rule refuses its
 *   export (I4). The draft is kept because it is work product; nothing
 *   downstream may treat it as sendable.
 * - `declined` — the pipeline refused this matter and named a reason
 *   ({@link DeclineReason}). Terminal, and a *pass* state: the platform
 *   answered honestly rather than failing.
 * - `held` — the machine's reading is ambiguous and a person must look. Not a
 *   decline, precisely because a decline names a reason and this cannot.
 * - `not_built` — a stage this matter needs is a port that has not landed.
 *   Neither a pass nor a failure, and the status line names the file.
 */
export const PIPELINE_VERDICTS = [
  'drafted',
  'blocked',
  'declined',
  'held',
  'not_built',
] as const
export type PipelineVerdict = (typeof PIPELINE_VERDICTS)[number]

/** `StageOutcome` — what one stage did. `not_built`/`skipped`/`stopped` end the run. */
export const STAGE_OUTCOMES = ['passed', 'noted', 'stopped', 'not_built', 'skipped'] as const
export type StageOutcome = (typeof STAGE_OUTCOMES)[number]

/**
 * `GateSeverity`. Three tiers, not two — `tm_gate` corrected the plan's
 * BLOCK/WARN split, and the order here is the order a findings list sorts in.
 */
export const GATE_SEVERITIES = ['BLOCK', 'WARN', 'NOTE'] as const
export type GateSeverity = (typeof GATE_SEVERITIES)[number]

/** `DeclineReason`. One of exactly four; each names a document, not an error. */
export const DECLINE_REASONS = [
  'NON_ENGLISH',
  'WIPO_IRREGULARITY',
  'OFFICE_AMBIGUOUS',
  'UNREADABLE_PDF',
] as const
export type DeclineReason = (typeof DECLINE_REASONS)[number]

/** `ReconcileOutcome` — what the two-source deadline comparison found (I7). */
export const RECONCILE_OUTCOMES = [
  'AGREED',
  'MISMATCH',
  'NO_RULE',
  'NO_PRINTED_DATE',
  'NO_INPUT',
] as const
export type ReconcileOutcome = (typeof RECONCILE_OUTCOMES)[number]

/**
 * One line of the run record.
 *
 * `detail` is written for a person reading a worklist, not for a log grepper,
 * so a screen renders it verbatim rather than deriving its own sentence from
 * `outcome`. `data` is the same facts in structured form — kept JSON-shaped
 * because it is also an event payload (I12).
 */
export interface PipelineStageResult {
  stage: string
  outcome: StageOutcome
  detail: string
  data: Record<string, unknown>
  /** Lines the reviewer's worklist (plan item 16) turns into CONSIDER entries. */
  considerations: string[]
}

/** One gate finding. `code` is `tm_gate`'s own spelling and is never localised. */
export interface GateFinding {
  severity: GateSeverity
  code: string
  detail: string
}

/**
 * The deadline cross-check (I7, REQ-1).
 *
 * Both dates travel, always. A screen that showed only `deadline_date` would
 * turn two sources into one, which is the entire failure this record exists to
 * prevent — and `blocks` is the server's word for it, never recomputed here
 * from `outcome` or from the flags.
 */
export interface DeadlineReconciliation {
  outcome: ReconcileOutcome
  rule_id: string | null
  rule_status: string | null
  blocks: boolean
  flags: string[]
  /** The date the matter is worked to: the earlier of the two when both exist. */
  deadline_date: string | null
  computed_date: string | null
  printed_date: string | null
}

/** One retrieval block's contribution. `empty` is a fact to show, not to hide. */
export interface RetrievalBlockSummary {
  count: number
  empty: boolean
}

/** `RetrievalBundle`, summarised — what the corpus gave this draft. */
export interface RetrievalBundleSummary {
  blocks: Record<string, RetrievalBlockSummary>
  /** How many distinct sources contributed across every block. */
  source_ids: number
  /** False means the embedder was unavailable and outcome weighting led (I10). */
  vector_led: boolean
}

/**
 * `LedgerEntryOut` — one contributing `custom_id`, resolved back to the
 * document (I2 / REQ-21). Mirrors `SourceMeta` field-for-field
 * (`server/app/routers/source.py`) because the ledger is `resolve_source`
 * read again, not a second implementation of what "openable" means.
 */
export interface LedgerEntry {
  custom_id: string
  /** `available` | `unusable` | `unknown` — `SourceResolution.status` verbatim. */
  status: string
  found: boolean
  openable: boolean
  office: string | null
  collection: string | null
  citability: string | null
  title: string | null
  cite: string | null
  doc_format: string | null
}

/**
 * `RunLedgerOut` — every source `bundle.source_ids` named, resolved live
 * rather than read back from storage: the stored bundle keeps only the
 * `custom_id` list (`_bundle_snapshot`), never the citation text, so this is
 * the one place that answers "what does this id actually open".
 */
export interface RunLedger {
  run_id: string
  matter_id: string
  entries: LedgerEntry[]
}

/** What the reviewer does about one worklist line, in the order they do it. */
export const WORKLIST_ACTIONS = ['STOP', 'VERIFY', 'SUPPLY', 'CONSIDER'] as const
export type WorklistAction = (typeof WORKLIST_ACTIONS)[number]

/** `WorklistItemOut` — one line, with the exact string to search the draft for. */
export interface WorklistItem {
  action: WorklistAction
  /** The rule that emitted this line, greppable in the server. */
  code: string
  /** The literal to search for — a ground code, a citation, a phrase, a block name. */
  detail: string
  minutes: number
  /** `gate` | `draft` | `retrieval` | `deadline` | `matter`. */
  source: string
  /** What to do about it, in a sentence. */
  gloss: string
}

/**
 * `WorklistOut` — what has to happen before this draft can go out.
 *
 * Derived by the server on every read from the findings, bundle,
 * reconciliation, draft and kind already on the run — never a stored column,
 * so it cannot summarise findings the run does not hold.
 *
 * `minutes_are_calibrated` is `false` today and the UI must say so wherever it
 * shows a total. The prototype's measured timings are not in the repository,
 * so these are estimates and borrowing authority they have not got is exactly
 * what I9 forbids.
 */
export interface Worklist {
  items: WorklistItem[]
  total_minutes: number
  stop_count: number
  minutes_are_calibrated: boolean
}

/** `PipelineRunOut` — everything one run did, in the order it did it. */
export interface PipelineRun {
  id: string
  matter_id: string
  status: PipelineRunStatus
  /**
   * The row still says `running` long after any real run could still be going.
   *
   * Derived by the server on every read rather than stored, so it is true the
   * instant it becomes true. It is what tells the run screen to stop polling: a
   * request that died mid-run leaves a `running` row behind by design, and
   * without this field a two-second poll spins on it forever.
   *
   * `status` is still `running` when this is true — the row has not been
   * rewritten yet. Read the pair together: "the record says running, and
   * nothing believes it any more". The next write to the run table closes it
   * into `failed`.
   */
  abandoned: boolean
  verdict: PipelineVerdict | null
  /** `PipelineRun.status` verbatim; its leading word is the verdict. */
  status_line: string
  office: string | null
  grounds: string[]
  kind: string | null
  /**
   * What the client was said to have told us, as the operator typed it — the
   * whole of `{case_facts}` and the gate's baseline for `FACT_ASSERTED`.
   *
   * `null` is not `''`. `null` means the run predates migration `0012` and
   * nobody knows what was supplied; `''` means a person supplied nothing, which
   * is why every unsourced claim in that draft was correctly flagged. A screen
   * that renders both as an empty box tells an attorney the last draft was
   * written with no client facts when it may only be that nobody recorded them.
   *
   * Present on the whole-run read, absent from the run list, which is a docket
   * view rather than a place for client-supplied material.
   */
  case_facts: string | null
  /** The server's answer, not a derivation. Only a clean draft may be sent. */
  exportable: boolean
  /** Whether a model call was actually made — a dry run answers false. */
  generated: boolean
  draft: string | null
  /** `draft` taken apart: the letter, and the drafter's notes for the reviewer
   * (`Grounds answered`, `Attorney checklist`, a citations line before the
   * letterhead). Derived on read; `null` exactly when `draft` is. Optional so a
   * server that predates it still types. */
  draft_parts?: {
    preamble: string
    letter: string
    grounds_answered: string
    checklist: string
  } | null
  stages: PipelineStageResult[]
  /** Derived on read; see {@link Worklist}. */
  worklist: Worklist
  gate_findings: GateFinding[]
  considerations: string[]
  decline_reason: DeclineReason | null
  reconciliation: DeadlineReconciliation | null
  bundle: RetrievalBundleSummary | null
  attempts: number
  started_at: string | null
  finished_at: string | null
  duration_ms: number | null
  error: string | null
  /**
   * Chunk 7 — whether a `draft.signed` event names this run. Derived on the
   * server on every read (`_run_out`), the same way `worklist` and
   * `abandoned` are — never cached here, so a page left open across a sign
   * elsewhere still shows the truth on the next poll.
   */
  signed: boolean
}

/**
 * `SignOut` — what `POST /api/pipeline/runs/{run_id}/sign` answers.
 *
 * `deadline_date` is the **re-verified** value (`server/app/services/sign.py`'s
 * whole reason for existing), not the one the drafting run recorded — the two
 * can differ only in the flags a stale clock would raise, never in the date
 * itself, but the distinction is the point of the endpoint.
 */
export interface SignResult {
  matter_id: string
  run_id: string
  deadline_date: string | null
  reconciled_event_id: number
  verified_event_id: number
  signed_event_id: number
}

/** `PipelineRunSummaryOut` — the list row. Newest first. */
export interface PipelineRunSummary {
  id: string
  matter_id: string
  status: PipelineRunStatus
  /** See {@link PipelineRun.abandoned}. The queue's chip is drawn from it. */
  abandoned: boolean
  verdict: PipelineVerdict | null
  status_line: string
  generated: boolean
  exportable: boolean
  gate_block_count: number
  started_at: string | null
  duration_ms: number | null
}

/**
 * `PipelineRunIn`.
 *
 * `generate` is the expensive half and defaults to false on the server as well
 * as in the form: a dry run walks every guard without spending a model call,
 * and that is the run somebody should be able to fire without thinking.
 */
export interface PipelineRunRequest {
  document_text: string
  grounds: string[]
  office: Jurisdiction | null
  kind: string | null
  doc_role: string
  report_date: string | null
  printed_deadline: string | null
  case_facts: string
  generate: boolean
  variant: string
  max_retries: number
}

// ---------------------------------------------------------------------------
// Corpus intelligence — chunk 9, server/app/routers/corpus_intel.py.
// Mirrors corpus_law.py's `{items, total, capped}` shape for anything that
// can exceed a server-side cap — a truncated answer must never read as a
// complete one (the no-silent-caps rule these types transcribe verbatim).
// ---------------------------------------------------------------------------

export interface ArgumentEntry {
  id: number
  custom_id: string
  proposition: string
  arg_type: string | null
  side: string | null
  task: string | null
  outcome_tag: string | null
  page: number | null
  ground: string
  inferred: boolean
}
export interface ArgumentsResponse {
  arguments: ArgumentEntry[]
  total: number
  capped: boolean
}

export interface PhraseEntry {
  id: number
  custom_id: string
  office: string | null
  ground: string | null
  move: string | null
  phrase: string
  n_uses: number | null
}
export interface PhrasesResponse {
  phrases: PhraseEntry[]
  total: number
  capped: boolean
}

export interface FlowStepEntry {
  /**
   * `null` when the row carries no office. The models declare it non-null and a
   * table the corpus dump recreated does not enforce that — see `_office_text`
   * in `corpus_intel.py`. Render the gap; never assume a string.
   */
  office: string | null
  doc_role: string
  move: string
  avg_position: number
  pct_of_documents: number
  n_documents: number | null
}
export interface FlowResponse {
  steps: FlowStepEntry[]
  total: number
  capped: boolean
}

/** `succeeded` means the **objection** succeeded — bad for the applicant. */
export const FINDING_RESULTS = ['succeeded', 'failed'] as const
export type FindingResult = (typeof FINDING_RESULTS)[number]

export interface GroundFindingEntry {
  id: number
  custom_id: string
  /**
   * `null` when the row carries no office. The models declare it non-null and a
   * table the corpus dump recreated does not enforce that — see `_office_text`
   * in `corpus_intel.py`. Render the gap; never assume a string.
   */
  office: string | null
  ground: string
  result: FindingResult | null
  reason: string | null
  page: number | null
}
export interface GroundFindingsResponse {
  findings: GroundFindingEntry[]
  total: number
  capped: boolean
}

export interface PatternEntry {
  id: number
  /**
   * `null` when the row carries no office. The models declare it non-null and a
   * table the corpus dump recreated does not enforce that — see `_office_text`
   * in `corpus_intel.py`. Render the gap; never assume a string.
   */
  office: string | null
  ground: string
  arg_type: string
  n_arguments: number
  exemplar: string | null
  exemplar_source: string | null
}
export interface PatternsResponse {
  patterns: PatternEntry[]
  total: number
  capped: boolean
}

export interface LessonEntry {
  id: number
  custom_id: string
  office: string | null
  ground: string | null
  lesson: string
  page: number | null
}
export interface LessonsResponse {
  lessons: LessonEntry[]
  total: number
  capped: boolean
}

/** `result === 'succeeded'` means the OBJECTION succeeded — bad for the
 * applicant, not good. Same inversion `GroundFindingEntry.result` carries. */
export interface DecisionEntry {
  id: number
  custom_id: string | null
  /**
   * `null` when the row carries no office. The models declare it non-null and a
   * table the corpus dump recreated does not enforce that — see `_office_text`
   * in `corpus_intel.py`. Render the gap; never assume a string.
   */
  office: string | null
  ground: string | null
  result: FindingResult | null
  outcome: string | null
  decided_date: string | null
  decision_ref: string | null
  tribunal: string | null
  mark_text: string | null
  case_id: number | null
}
export interface DecisionsResponse {
  decisions: DecisionEntry[]
  total: number
  capped: boolean
}

/** One pre-approved goods/services description within a Nice class — no
 * office, no custom_id: treaty reference data, not extracted from a filing. */
export interface NiceClassEntry {
  id: number
  class_number: number
  class_heading: string
  description: string
}
export interface NiceClassesResponse {
  classes: NiceClassEntry[]
  total: number
  capped: boolean
}

// ---------------------------------------------------------------------------
// Ask-with-citations — chunk 9, server/app/routers/ask.py. An internal
// research tool (research:read), not a client-facing artifact — so a
// citation drawn from tier-2 (`stats_only`) material is surfaced, with its
// tier, rather than withheld the way I8 requires for anything a client sees.
// ---------------------------------------------------------------------------

export interface AskRequest {
  office: Jurisdiction
  grounds: string[]
  question: string
}

export interface AskCitation {
  custom_id: string
  quote: string
  kind: 'law' | 'phrase' | 'argument' | 'authority'
  citability: 'citable' | 'stats_only'
}

export interface AskRejectedCitation {
  custom_id: string
  quote: string
  reason: 'unknown_source' | 'quote_not_found'
}

export interface AskResponse {
  question: string
  office: string
  grounds: string[]
  answer: string
  citations: AskCitation[]
  rejected: AskRejectedCitation[]
  sources_considered: number
  /** Whether the four reads were ranked by the question's own embedding, or
   * fell back to their unranked office/ground order (no embedder
   * configured) — RetrievalBundle.vector_led's own field, same name. */
  vector_led: boolean
}

// ---------------------------------------------------------------------------
// Nice-class suggestion — server/app/routers/nice_classifier.py. Its own
// file server-side because it spends a completion; its own section here for
// the same reason Ask gets one, despite being a different router.
// ---------------------------------------------------------------------------

export interface SuggestNiceClassRequest {
  description: string
}

export interface NiceClassChoice {
  id: number
  class_number: number
  class_heading: string
  description: string
  reasoning: string
}

export interface NiceClassRejectedChoice {
  id: number
  reasoning: string
  reason: string
}

export interface SuggestNiceClassResponse {
  query: string
  choices: NiceClassChoice[]
  rejected: NiceClassRejectedChoice[]
  candidates_considered: number
  /** False when no embedder was available to rank candidates at all — in
   * which case candidates_considered is always 0 and no completion was
   * spent. See suggest_nice_classes's own docstring. */
  semantic_search: boolean
}

// ---------------------------------------------------------------------------
// Statistics — server/app/routers/stats.py (already built; chunk 9 only adds
// a reader). Every rate travels with `caveat` — I9's whole discipline is that
// a number is never shown without the sentence that qualifies it.
// ---------------------------------------------------------------------------

export interface RegisteredQuery {
  query_id: string
  description: string
  office: string
  ground: string | null
  basis: 'population' | 'own_docket' | 'corpus_sample'
  source_table: string | null
  refuses_rate: boolean
  caveat: string
  version: number
  enabled: boolean
  aliases: string[]
  sql: string
  sql_fingerprint: string
}

export interface RegisteredQueryList {
  registry_version: number
  count: number
  queries: RegisteredQuery[]
}

/** `run_query`'s payload — no fixed `response_model` server-side (its own
 * module docstring explains why: the dict is built once and re-declaring its
 * shape here would be a second definition free to drift). Read defensively. */
export interface StatQueryResult {
  query_id: string
  n: number | null
  favourable: number | null
  rate: number | null
  low_confidence: boolean
  by_exit_route: Record<string, unknown> | null
  caveat: string
  refused: boolean
  refusal_reason?: string
  computed_at: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// The per-matter ledger — chunk 9's cross-run sibling of chunk 6's per-run
// `RunLedger`. Same `LedgerEntry` shape, plus `run_ids` naming which of the
// matter's runs actually cited each source.
// ---------------------------------------------------------------------------

export interface MatterLedgerEntry extends LedgerEntry {
  run_ids: string[]
}

export interface MatterLedger {
  matter_id: string
  entries: MatterLedgerEntry[]
  run_count: number
}

// ---------------------------------------------------------------------------
// Queue lanes, SLA clocks, BD gate, digest — chunk 10,
// server/app/routers/queue.py. `lane` and every count here are the server's
// own answer (I4's "the second gate is always the lenient one" applies just
// as much to a lane assignment as to `exportable`); nothing in this module
// re-derives a lane from a run's fields.
// ---------------------------------------------------------------------------

export const LANES = ['ready', 'flagged'] as const
export type Lane = (typeof LANES)[number]

export interface QueueRow {
  matter_id: string
  jurisdiction: string
  lifecycle: string
  lane: Lane
  exportable: boolean
  deadline_date: string | null
  deadline_verified: boolean
  /** Only ever non-null when `deadline_verified` is true — an unverified
   * date is not something anyone has attested is honest to count down to. */
  sla_days_remaining: number | null
  bd_gate_pending: boolean
  pack_approved: boolean
  /** Who opened the matter. Every caller receives it, but only an admin's
   * queue ever holds rows that are not their own, so only the admin's screen
   * shows the column. `null` for a matter parked with the administrators. */
  owner_actor: string | null
}

export interface QueueResponse {
  date: string
  rows: QueueRow[]
}

export interface DigestCounts {
  total_matters: number
  active: number
  ready: number
  flagged: number
  bd_gate_pending: number
  sla_overdue: number
  sla_due_soon: number
  /** Lifecycle DECLINED. Always a pass — never counted as a failure
   * anywhere in this payload (there is no "failed" field on this type). */
  declined: number
  dispatched: number
  /** declined + dispatched — the whole "declines count as PASS-with-reason"
   * contract, made structural rather than only a comment. */
  passed: number
}

export interface Digest {
  date: string
  counts: DigestCounts
  ready_matter_ids: string[]
  flagged_matter_ids: string[]
}

export interface BatchApproveSkip {
  matter_id: string
  reason: string
}

export interface BatchApproveResult {
  approved: string[]
  skipped: BatchApproveSkip[]
}

export interface BdGateClearResult {
  matter_id: string
  cleared_event_id: number
}
